import { Suspense, useDeferredValue } from 'react'
import { createFileRoute, Link, useMatch } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import type { inferRouterOutputs } from '@trpc/server'
import type { AppRouter } from 'server/trpc'
import { cn } from '@/lib/utils'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { SETTINGS_STACK } from './-components/settings/settings-section'
import { LINK_CLASS } from './-components/admin/traces-shared'
import { formatCount, formatMs, formatPct, formatRate } from './-components/admin/metrics-format'
import {
  CHART_ROW,
  ChartRowSkeleton,
  OverviewPending,
  TableSkeleton,
  TILE_GRID,
  TilesSkeleton,
} from './-components/admin/metrics-pending'
import {
  Chip,
  DeltaChip,
  EmptyPanel,
  ErrorRateChart,
  KindBadge,
  LatencyBar,
  LatencyChart,
  MetricsSection,
  SMALL_PLOT,
  SOURCE_LABELS,
  TelemetryNotice,
  ThroughputChart,
  Tile,
} from './-components/admin/metrics-panels'

// The metrics' first screen: the service's health against the previous period, its traffic at a glance, and the
// five slowest endpoints and five commonest errors, each leading to its own screen.

export const Route = createFileRoute('/_app/admin/metrics/_overview')({
  component: RouteComponent,
  pendingComponent: OverviewPending,
})

type Outputs = inferRouterOutputs<AppRouter>['admin']
type Endpoint = Outputs['metricsEndpoints']['endpoints'][number]

function useWindowContext() {
  return useMatch({ from: '/_app/admin/metrics/_overview/_window' }).context
}

function RouteComponent() {
  return (
    <div className={SETTINGS_STACK}>
      <Suspense fallback={null}>
        <Notice />
      </Suspense>
      <MetricsSection copy="health">
        <div className="flex flex-col gap-3">
          <Suspense fallback={<TilesSkeleton count={8} />}>
            <HealthTiles />
          </Suspense>
          <Suspense fallback={<ChartRowSkeleton count={3} />}>
            <TrafficRow />
          </Suspense>
        </div>
      </MetricsSection>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <MetricsSection
          copy="slowest"
          actions={
            <Link to="/admin/metrics/endpoints" search={true} className={cn(LINK_CLASS, 'text-xs')}>
              All endpoints
            </Link>
          }
        >
          <Suspense fallback={<TableSkeleton rows={5} />}>
            <SlowestEndpoints />
          </Suspense>
        </MetricsSection>
        <MetricsSection
          copy="topErrors"
          actions={
            <Link to="/admin/metrics/errors" search={true} className={cn(LINK_CLASS, 'text-xs')}>
              All errors
            </Link>
          }
        >
          <Suspense fallback={<TableSkeleton rows={5} />}>
            <TopErrors />
          </Suspense>
        </MetricsSection>
      </div>
    </div>
  )
}

function Notice() {
  const { data } = useSuspenseQuery(useWindowContext().metricsKpisQueryOptions)
  return <TelemetryNotice state={data.clickhouse} />
}

function HealthTiles() {
  const context = useWindowContext()
  const options = useDeferredValue(context.metricsKpisQueryOptions)
  const { data } = useSuspenseQuery(options)
  const stale = options !== context.metricsKpisQueryOptions
  const offline = data.clickhouse !== 'ok'
  return (
    <div className={cn(TILE_GRID, 'transition-opacity', stale && 'opacity-60')}>
      <Tile
        label="Requests / min"
        figure={data.requestsPerMin}
        format={formatRate}
        better="neutral"
        offline={offline}
      />
      <Tile
        label="Server error rate"
        figure={data.errorRatePct}
        format={formatPct}
        better="down"
        offline={offline}
        hint="5xx or an exception"
      />
      <Tile label="API p95 · server" figure={data.apiP95Ms} format={formatMs} better="down" offline={offline} />
      <Tile
        label="API p95 · in the browser"
        figure={data.clientP95Ms}
        format={formatMs}
        better="down"
        offline={offline}
        empty="no browser timings yet"
      />
      <Tile label="Page render p95" figure={data.ssrP95Ms} format={formatMs} better="down" offline={offline} />
      <Tile label="Sessions" figure={data.sessions} format={formatCount} better="up" offline={offline} />
      <Tile label="Signed-in users" figure={data.users} format={formatCount} better="up" offline={offline} />
      <Tile label="Assistant turns" figure={data.assistantTurns} format={formatCount} better="up" offline={offline} />
    </div>
  )
}

function TrafficRow() {
  const context = useWindowContext()
  const options = useDeferredValue(context.metricsTrafficQueryOptions)
  const { data } = useSuspenseQuery(options)
  const stale = options !== context.metricsTrafficQueryOptions
  return (
    <div className={cn(CHART_ROW, 'transition-opacity', stale && 'opacity-60')}>
      <ThroughputChart data={data} height={SMALL_PLOT} />
      <ErrorRateChart data={data} height={SMALL_PLOT} />
      <LatencyChart data={data} height={SMALL_PLOT} />
    </div>
  )
}

// MARK: Teasers

const TEASER_ROWS = 5
// Below this many calls a p95 is a handful of samples: one slow cold start would top the list.
const MIN_CALLS_TO_RANK = 20

/** The worst p95s among the endpoints called often enough for a p95 to mean something. */
function slowest(endpoints: Endpoint[]): Endpoint[] {
  const ranked = endpoints.filter((row) => row.calls >= MIN_CALLS_TO_RANK)
  return [...(ranked.length > 0 ? ranked : endpoints)].sort((a, b) => b.p95 - a.p95).slice(0, TEASER_ROWS)
}

function SlowestEndpoints() {
  const context = useWindowContext()
  const options = useDeferredValue(context.metricsEndpointsQueryOptions)
  const { data } = useSuspenseQuery(options)
  const stale = options !== context.metricsEndpointsQueryOptions
  const rows = slowest(data.endpoints)
  if (rows.length === 0)
    return (
      <EmptyPanel>{data.clickhouse === 'ok' ? 'Nothing was called in this window.' : 'No traffic to rank.'}</EmptyPanel>
    )
  const maxP99 = Math.max(1, ...rows.map((row) => row.p99))
  return (
    <div className={cn('scrollbar overflow-x-auto rounded-xl border transition-opacity', stale && 'opacity-60')}>
      <Table className="min-w-[440px] table-fixed border-collapse">
        <TableHeader className="bg-muted/50">
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-muted-foreground px-3 py-2">Endpoint</TableHead>
            <TableHead className="text-muted-foreground w-20 px-3 py-2 text-right">Rate</TableHead>
            <TableHead className="text-muted-foreground w-20 px-3 py-2 text-right">p95</TableHead>
            <TableHead className="text-muted-foreground w-24 px-3 py-2 text-right">p99</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.kind}:${row.endpoint}`}>
              <TableCell className="px-3 py-2">
                <div className="flex min-w-0 items-center gap-2">
                  <KindBadge kind={row.kind} />
                  <span className="truncate font-mono text-xs" title={row.endpoint}>
                    {row.endpoint}
                  </span>
                </div>
              </TableCell>
              <TableCell className="px-3 py-2 text-right text-xs tabular-nums">
                {formatRate(row.ratePerMin)}/min
              </TableCell>
              <TableCell className="px-3 py-2 text-right text-xs tabular-nums">
                <div className="flex flex-col items-end">
                  <span>{formatMs(row.p95)}</span>
                  <DeltaChip deltaPct={row.p95DeltaPct} better="down" className="text-[10px]" />
                </div>
              </TableCell>
              <TableCell className="px-3 py-2">
                <LatencyBar ms={row.p99} max={maxP99} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function TopErrors() {
  const context = useWindowContext()
  const options = useDeferredValue(context.metricsErrorsQueryOptions)
  const { data } = useSuspenseQuery(options)
  const stale = options !== context.metricsErrorsQueryOptions
  const rows = data.errors.slice(0, TEASER_ROWS)
  if (rows.length === 0)
    return <EmptyPanel>{data.clickhouse === 'ok' ? 'No error in this window.' : 'No errors to rank.'}</EmptyPanel>
  return (
    <div className={cn('scrollbar overflow-x-auto rounded-xl border transition-opacity', stale && 'opacity-60')}>
      <Table className="min-w-[440px] table-fixed border-collapse">
        <TableHeader className="bg-muted/50">
          <TableRow className="hover:bg-transparent">
            <TableHead className="text-muted-foreground px-3 py-2">Error</TableHead>
            <TableHead className="text-muted-foreground w-20 px-3 py-2 text-right">Events</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.source}:${row.type}:${row.message}`}>
              <TableCell className="px-3 py-2">
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="flex min-w-0 items-center gap-2">
                    <Chip>{SOURCE_LABELS[row.source]}</Chip>
                    <span className="truncate text-xs font-medium">{row.type}</span>
                  </span>
                  <span className="text-muted-foreground truncate text-xs" title={row.message}>
                    {row.message || row.where || '—'}
                  </span>
                </div>
              </TableCell>
              <TableCell className="px-3 py-2 text-right text-xs tabular-nums">
                <div className="flex flex-col items-end">
                  <span className="font-medium">{formatCount(row.count)}</span>
                  <DeltaChip deltaPct={row.countDeltaPct} better="down" className="text-[10px]" />
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}
